// @ts-strict-ignore
import { requestApi } from 'api/api'
import { FiatPayout, MassPayoutCryptoBatch } from 'pages/MassPayouts/types'
import { WithPaginationApiResponse } from 'types/pagination'
import { DocumentUpload } from 'types/verification'

//------------------- CRYPTO --------------------------------------------------------------

interface PostMassCryptoPayoutsRequest {
  walletName: string
  walletId: string
  file: DocumentUpload
}

export const postMassCryptoPayouts = ({ walletId, walletName, file }: PostMassCryptoPayoutsRequest) => {
  const formData = new FormData()

  formData.append('walletName', walletName) 
  formData.append('walletId', walletId)
  formData.append('file', file.fileData)

  return requestApi({
    url: '/v1/pay/masspayout/new',
    method: 'POST',
    data: formData,
  })
}

export const getAllCryptoMassPayouts = (): Promise<MassPayoutCryptoBatch[]> => {
  return requestApi({
    url: '/v1/pay/masspayout',
    method: 'GET',
    params: {
      max: 20,
      sort: 'createdAt',
      order: 'desc',
    },
  })
}

export const getAllCryptoMassPayoutsByPolling = (): Promise<MassPayoutCryptoBatch[]> => {
  return requestApi({
    url: `/v1/pay/masspayout/status`,
    params: {
      max: 20,
      sort: 'createdAt',
      order: 'desc',
    },
    method: 'GET',
  })
}

interface GetCryptoMassPayoutByIdRequest {
  batchId: string
}

export const getCryptoMassPayoutById = ({
  batchId,
}: GetCryptoMassPayoutByIdRequest): Promise<MassPayoutCryptoBatch> => {
  return requestApi({
    url: `/v1/pay/masspayout/${batchId}?status=DRAFT,ERROR`,
    method: 'GET',
  })
}

interface DeleteCryptoPayoutByIdRequest {
  batchId: string
  externalId: string
}

export const deleteCryptoPayoutById = ({ batchId, externalId }: DeleteCryptoPayoutByIdRequest) => {
  return requestApi({
    url: `/v1/pay/masspayout/${batchId}/items/${externalId}`,
    method: 'DELETE',
  })
}

interface PutUpdateCryptoPayoutByIdRequest {
  batchId: string
  externalId: string
  fundingWalletAmount?: string
  paidCurrencyAmount?: string
  currency: string
  protocol?: string
  network?: string
  address: string
  reference: string
  tag?: string
  onlyValidate?: boolean
  contactId?: string
}

export const putUpdateCryptoPayoutById = ({
  batchId,
  externalId,
  fundingWalletAmount,
  paidCurrencyAmount,
  currency,
  protocol,
  network,
  address,
  reference,
  tag = '',
  contactId,
  onlyValidate = false,
}: PutUpdateCryptoPayoutByIdRequest) => {
  return requestApi({
    url: `/v1/pay/masspayout/${batchId}/items/${externalId}`,
    method: 'PUT',
    params: {
      ...(onlyValidate && { onlyValidate }),
    },
    data: {
      fundingWalletAmount,
      paidCurrencyAmount,
      currency,
      ...(protocol && { protocol }),
      ...(network && { network }),
      address,
      reference,
      tag,
      ...(contactId && { contactId }),
    },
  })
}

interface PutSubmitCryptoMassPayoutByIdRequest {
  batchId: string
}

export const putSubmitCryptoMassPayoutById = ({
  batchId,
}: PutSubmitCryptoMassPayoutByIdRequest) => {
  return requestApi({
    url: `/v1/pay/masspayout/${batchId}`,
    method: 'PUT',
  })
}

//-------------------------------------- FIAT --------------------------------------

interface MassFiatPayoutRequest {
  walletId: string
  file: DocumentUpload
}

export const postMassFiatPayouts = ({ walletId, file }: MassFiatPayoutRequest) => {
  const formData = new FormData()

  formData.append('walletId', walletId)
  formData.append('file', file.fileData)

  return requestApi({
    url: '/payment/v1/payouts/batch',
    baseUrl: process.env.API_URL,
    method: 'POST',
    data: formData,
  })
}

export const patchMassFiatPayoutsPreChallengeRequest = (batchReference: string) => {
  return requestApi({
    url: `/payment/v1/payouts/batch/${batchReference}/trigger`,
    baseUrl: process.env.API_URL,
    method: 'PATCH',
  })
}

export interface getMassFiatPayoutsRecordsRequest {
  batchReference: string
  size?: number
  page?: number
}

export const getMassFiatPayoutsRecords = ({
  batchReference,
  size = 10,
  page = 0,
}: getMassFiatPayoutsRecordsRequest): Promise<WithPaginationApiResponse<FiatPayout>> => {
  return requestApi({
    url: `/payment/v1/payouts/batch/${batchReference}/payments`,
    baseUrl: process.env.API_URL,
    method: 'GET',
    params: {
      page,
      sort: 'createdAt,desc',
      ...(size && { size }),
    },
  })
}

export interface MassFiatPayoutsDetailsRequest {
  batchReference: string
}

export const getMassFiatPayoutsDetails = ({
  batchReference,
}: MassFiatPayoutsDetailsRequest): Promise<FiatPayout> => {
  return requestApi({
    url: `/payment/v1/payouts/batch/${batchReference}`,
    baseUrl: process.env.API_URL,
    method: 'GET',
  })
}

export const getMassFiatPayoutsList = ({
  size,
  page,
}: {
  size: number
  page: number
}): Promise<WithPaginationApiResponse<FiatPayout[]>> => {
  return requestApi({
    url: `/payment/v1/payouts/batch`,
    baseUrl: process.env.API_URL,
    method: 'GET',
    params: {
      page,
      sort: 'createdAt,desc',
      ...(size && { size }),
    },
  })
}

export const patchPreprocessedRecords = async ({ reference, data }) => {
  return requestApi({
    url: `/payment/v1/payouts/batch/record/${reference}`,
    baseUrl: process.env.API_URL,
    method: 'PATCH',
    data,
  })
}

export const deletePreprocessedRecords = async ({ reference }) => {
  return requestApi({
    url: `/payment/v1/payouts/batch/record/${reference}`,
    baseUrl: process.env.API_URL,
    method: 'DELETE',
  })
}
